import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MonthRangeSelector, rangeForPreset, type MonthRangePreset } from '@/components/publish/MonthRangeSelector';
import { PublishStatusMatrix } from '@/components/publish/PublishStatusMatrix';
import { BulkActionsToolbar, type SelectedCell } from '@/components/publish/BulkActionsToolbar';
import { PublishCellDetailModal } from '@/components/publish/PublishCellDetailModal';
import { fetchPublishStatus, postPublish, postUnpublish } from '@/lib/api/publish';
import { useEffectivePermissions } from '@/hooks/useEffectivePermissions';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/stores/toast';

/**
 * v0.3 (S-7): データ公開ゲート管理画面。
 * 設計: docs/design/v03-data-publish-gate-detail.md §9.2
 */
export function PublishManagementPage() {
  const [preset, setPreset] = useState<MonthRangePreset>('6m');
  const [selected, setSelected] = useState<Map<string, SelectedCell>>(new Map());
  const [detailTarget, setDetailTarget] = useState<{ nodeId: string; nodeName: string; targetMonth: string } | null>(null);
  const qc = useQueryClient();
  const { hasPermissionForFeatureAction } = useEffectivePermissions();
  const tenantFeatures = useAuthStore((s) => s.user?.tenant_features);

  const { from, to } = useMemo(() => rangeForPreset(preset), [preset]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['publish-status', from, to],
    queryFn: () => fetchPublishStatus({ year_month_from: from, year_month_to: to }),
    enabled: tenantFeatures?.publish_gate_enabled !== false,
  });

  const canSelect = hasPermissionForFeatureAction('publish', 'publish') || hasPermissionForFeatureAction('publish', 'unpublish');

  const toggleSelect = (nodeId: string, month: string) => {
    const key = `${nodeId}|${month}`;
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else {
        const nodeName = data?.nodes.find((n) => n.node_id === nodeId)?.node_name ?? nodeId;
        next.set(key, { nodeId, nodeName, targetMonth: month });
      }
      return next;
    });
  };

  const bulkPublish = async (cells: SelectedCell[]) => {
    // R-S7-02対応: 一括操作は直列実行 (並列化によるレース回避)
    for (const cell of cells) {
      // eslint-disable-next-line no-await-in-loop
      try {
        await postPublish({ node_id: cell.nodeId, target_month: cell.targetMonth, allow_partial: true });
      } catch {
        toast.error(`${cell.nodeName} ${cell.targetMonth} の公開に失敗しました`);
      }
    }
    qc.invalidateQueries({ queryKey: ['publish-status'] });
    toast.success('一括公開が完了しました');
  };

  const bulkUnpublish = async (cells: SelectedCell[]) => {
    for (const cell of cells) {
      // eslint-disable-next-line no-await-in-loop
      try {
        await postUnpublish({ node_id: cell.nodeId, target_month: cell.targetMonth });
      } catch {
        toast.error(`${cell.nodeName} ${cell.targetMonth} の非公開化に失敗しました`);
      }
    }
    qc.invalidateQueries({ queryKey: ['publish-status'] });
    toast.success('一括非公開が完了しました');
  };

  if (tenantFeatures?.publish_gate_enabled === false) {
    return (
      <div className="p-4 sm:p-6">
        <p className="text-[14px] text-text-sub">この機能はテナントで無効化されています。</p>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[20px] font-extrabold text-text-main tracking-tight">データ公開管理</h1>
        <MonthRangeSelector value={preset} onChange={setPreset} />
      </div>

      {isLoading && <p className="text-[13px] text-text-sub">読み込み中…</p>}
      {isError && <p className="text-[13px] text-danger-text">データの取得に失敗しました</p>}

      {data && (
        <PublishStatusMatrix
          nodes={data.nodes}
          months={data.months}
          selectedCells={new Set(selected.keys())}
          onToggleSelect={toggleSelect}
          onCellClick={(nodeId, targetMonth) => {
            const nodeName = data.nodes.find((n) => n.node_id === nodeId)?.node_name ?? nodeId;
            setDetailTarget({ nodeId, nodeName, targetMonth });
          }}
          canSelect={canSelect}
        />
      )}

      <BulkActionsToolbar
        selected={[...selected.values()]}
        canPublish={hasPermissionForFeatureAction('publish', 'publish')}
        canUnpublish={hasPermissionForFeatureAction('publish', 'unpublish')}
        onPublish={bulkPublish}
        onUnpublish={bulkUnpublish}
        onClearSelection={() => setSelected(new Map())}
      />

      {detailTarget && (
        <PublishCellDetailModal
          open={detailTarget !== null}
          nodeId={detailTarget.nodeId}
          nodeName={detailTarget.nodeName}
          targetMonth={detailTarget.targetMonth}
          onClose={() => setDetailTarget(null)}
        />
      )}
    </div>
  );
}
